import { ActionBar, cn, Modal, RolesIcon, Text } from '@viapeople/components'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { type Column, GenericTable } from '../../components/GenericTable'
import { trpc } from '../../utils/trpc'
import type { Role, RoleListInput } from '../roles/types'

export interface RolesModalProps {
	isOpen: boolean
	onClose: () => void
	onAddRoles: (roles: Role[]) => void
	organizationId: string
	ignoredRoles?: string[]
}

export const AddRolesModal = ({ isOpen, onClose, onAddRoles, organizationId, ignoredRoles }: RolesModalProps) => {
	const [selectedRoles, setSelectedRoles] = useState<Role[]>([])
	const [isEmpty, setIsEmpty] = useState(false)
	const navigate = useNavigate()

	const columns: Column<Role>[] = [
		{
			field: 'name',
			label: 'Role Name',
			colSpan: 2,
			render: (role: Role) => (
				<Text variant='xsmall' weight='regular'>
					{role.name}
				</Text>
			),
		},
		{
			field: 'displayName',
			label: 'Display Name',
			colSpan: 2,
			render: (role: Role) => (
				<Text variant='xsmall' weight='regular'>
					{role.displayName}
				</Text>
			),
		},
		{
			field: 'description',
			label: 'Description',
			colSpan: 2,
			render: (role: Role) => (
				<Text variant='xsmall' weight='regular'>
					{role.description}
				</Text>
			),
		},
	]

	const handleSelectionChange = (roles: Role[]) => {
		setSelectedRoles(roles)
	}

	const handleAddRoles = () => {
		onAddRoles(selectedRoles)
		onClose()
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title='Select Roles'
			fullContent
			className='md:w-300 max-w-[1110px] mx-12 rounded-md overflow-hidden'>
			<GenericTable<Role, RoleListInput>
				columns={columns}
				idField='id'
				isInternal
				className={cn('min-h-[460px]', { 'min-h-0': isEmpty })}
				onStatusChange={(isEmpty) => setIsEmpty(isEmpty)}
				bodyClassName='max-h-[300px] overflow-y-auto'
				selectable
				onSelectionChange={handleSelectionChange}
				queryOptionsGenerator={(options) => ({
					...trpc.admin.roles.search.queryOptions({
						...options,
						organizationId: organizationId,
						notIn: ignoredRoles,
					}),
				})}
				showSearch
				showPagination
				emptyState={{
					title: 'No available roles',
					description: 'Please add roles to the organization',
					icon: RolesIcon,
					actionLabel: 'Add Role',
					onAction: () => {
						navigate(`/organizations/${organizationId}/roles`)
					},
				}}
			/>
			{!isEmpty && (
				<ActionBar
					variant='modal'
					className='relative left-0 border-0'
					rightActions={[
						{
							variant: 'secondary',
							label: 'Cancel',
							onClick: onClose,
						},
						{
							variant: 'primary',
							label: `Add Roles (${selectedRoles.length})`,
							onClick: handleAddRoles,
							disabled: selectedRoles.length === 0,
						},
					]}
				/>
			)}
		</Modal>
	)
}
